import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, FileText, Printer, Download as DownloadIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import taxInvoiceService from '../../../services/taxInvoiceService';
import customerMasterService from '../../../services/customerMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';
import { formatCurrency } from '../../../utils/currencyFormat';
import * as XLSX from 'xlsx';

const CURRENCY_ORDER = ['INR', 'USD', 'EUR', 'GBP', 'CAD', 'AUD'];

const formatAmount = (amount, currency = 'INR') => formatCurrency(amount || 0, currency || 'INR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const SalesRegisterReport = () => {
  const navigate = useNavigate();
  
  // Get current month start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Format dates as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    
    return {
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate)
    };
  };

  const [filters, setFilters] = useState({
    customer: '',
    fromDate: getCurrentMonthDates().fromDate,
    toDate: getCurrentMonthDates().toDate
  });
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customerMasterService.getAllCustomers({ limit: 1000 });
        if (response && response.success) {
          setCustomers(response.data || []);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await taxInvoiceService.getAllTaxInvoices({
        page: 1,
        limit: 10000, // Get all records for report
        status: 'Approved', // Only show approved invoices
        startDate: filters.fromDate,
        endDate: filters.toDate,
        customer: filters.customer || undefined
      });

      if (response && response.success) {
        const transformedData = (response.data || []).map(invoice => ({
          ...invoice,
          id: invoice._id || invoice.id
        }));
        setInvoices(transformedData);
      } else {
        toast.error('Failed to load sales register report');
      }
    } catch (error) {
      console.error('Error loading sales register:', error);
      toast.error('Error loading sales register report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    loadReportData();
  };

  const handleResetFilter = () => {
    const currentMonth = getCurrentMonthDates();
    setFilters({
      customer: '',
      fromDate: currentMonth.fromDate,
      toDate: currentMonth.toDate
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Font sizes for report
  const reportFontSize = {
    footerLabel: '14px'
  };

  // Each currency is totaled on its own. A USD invoice is never folded into the INR total.
  const currencyTotals = useMemo(() => {
    const grouped = invoices.reduce((acc, invoice) => {
      const code = invoice.currency || 'INR';
      if (!acc[code]) {
        acc[code] = {
          currency: code,
          taxable: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          gross: 0
        };
      }
      acc[code].taxable += invoice.totalValue || 0;
      acc[code].cgst += invoice.totalCGSTAmount || 0;
      acc[code].sgst += invoice.totalSGSTAmount || 0;
      acc[code].igst += invoice.totalIGSTAmount || 0;
      acc[code].gross += invoice.totalAmountWithTax || 0;
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const aIndex = CURRENCY_ORDER.indexOf(a.currency);
      const bIndex = CURRENCY_ORDER.indexOf(b.currency);
      return (aIndex === -1 ? CURRENCY_ORDER.length : aIndex) - (bIndex === -1 ? CURRENCY_ORDER.length : bIndex);
    });
  }, [invoices]);

  const totalsToShow = currencyTotals.length
    ? currencyTotals
    : [{ currency: 'INR', taxable: 0, cgst: 0, sgst: 0, igst: 0, gross: 0 }];

  const renderTotal = (pick) => (
    <div className="sr-total-figures">
      {totalsToShow.map((row) => (
        <div key={row.currency} className="sr-total-line">
          {totalsToShow.length > 1 && <span className="sr-total-code">{row.currency}</span>}
          <span>{formatAmount(pick(row), row.currency)}</span>
        </div>
      ))}
    </div>
  );

  // Define table columns
  const columns = [
    {
      key: 'serviceInvoiceDate',
      label: 'Doc. Date',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true
    },
    {
      key: 'customerGSTIN',
      label: 'GSTIN',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'serviceInvoiceNumber',
      label: 'Doc. No.',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      align: 'center',
      render: (value) => value || 'INR'
    },
    {
      key: 'totalValue',
      label: 'Taxable Value',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalCGSTAmount',
      label: 'CGST- 9%',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalSGSTAmount',
      label: 'SGST- 9%',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalIGSTAmount',
      label: 'IGST - 18%',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalAmountWithTax',
      label: 'Gr. Sales Value',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: () => null,
      width: '0px',
      style: { display: 'none' }
    }
  ];

  const handleExportToExcel = () => {
    try {
      const exportData = invoices.map(invoice => ({
        'Doc. Date': formatDate(invoice.serviceInvoiceDate),
        'Customer Name': invoice.customerName || '',
        'GSTIN': invoice.customerGSTIN || '',
        'Doc. No.': invoice.serviceInvoiceNumber || '',
        'Currency': invoice.currency || 'INR',
        'Taxable Value': invoice.totalValue || 0,
        'CGST- 9%': invoice.totalCGSTAmount || 0,
        'SGST- 9%': invoice.totalSGSTAmount || 0,
        'IGST - 18%': invoice.totalIGSTAmount || 0,
        'Gr. Sales Value': invoice.totalAmountWithTax || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Register');
      XLSX.writeFile(wb, `Sales_Register_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Sales register exported to Excel successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Reports',
      path: '/sales?tab=reports',
      onClick: () => navigate('/sales?tab=reports')
    },
    {
      label: 'Sales Register',
      path: '/sales/reports/sales-register',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1 style={{ fontSize: '22px' }}>Sales Register</h1>
          <p style={{ fontSize: '12px' }}>Comprehensive sales register and transaction reports</p>
        </div>
      </div>

      {/* Filters Section - Compact, one row */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        marginLeft: '0',
        marginRight: '0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.customerName} ({customer.customerCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>From Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>To Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleApplyFilter}
            style={{ 
              padding: '6px 10px',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Apply Filter"
          >
            <Filter size={16} />
          </button>

          <button
            className="btn-secondary"
            onClick={handleResetFilter}
            style={{ 
              padding: '6px 10px',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Reset Filter"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div style={{ 
        marginBottom: '80px', 
        marginLeft: '0', 
        marginRight: '0',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        <DataTable
          data={invoices}
          columns={columns}
          loading={loading}
          noCard={true}
          className="sales-register-report-table"
          pageSize={50}
          customActions={[]}
        />
      </div>

      {/* Summary Footer - Fixed at bottom, matching table width */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '314px', // Sidebar (250px) + left padding (64px)
        right: '64px', // Right padding to match sales-dashboard
        background: '#E8E1F5',
        padding: '14px 20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '20px',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        zIndex: 100,
        borderRadius: '0',
        borderTop: '2px solid #E8E1F5'
      }}>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> Total Taxable Value
          </div>
          {renderTotal((row) => row.taxable)}
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> CGST %
          </div>
          {renderTotal((row) => row.cgst)}
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> SGST %
          </div>
          {renderTotal((row) => row.sgst)}
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> IGST %
          </div>
          {renderTotal((row) => row.igst)}
        </div>
        <div>
          <div style={{ fontSize: reportFontSize.footerLabel, color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>→</span> Gr. Sales Value
          </div>
          {renderTotal((row) => row.gross)}
        </div>
      </div>
    </div>
  );
};

export default SalesRegisterReport;

